// Intentionally divergent — see /COMPONENT-DIVERGENCE.md for rationale
import { useRef, type ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '@/lib/utils'

export interface VirtualColumnDef {
  key: string
  header: string
  /** Raw grid track; wins over `minWidth` and `fr` when set. */
  width?: string
  /** Pixels; with `fr` the track is `minmax(<minWidth>px, <fr>fr)`. */
  minWidth?: number
  /** Share of the free space; defaults to 1. */
  fr?: number
  className?: string
}

/**
 * One grid track per column. A column with neither `width`, `minWidth` nor `fr`
 * keeps the plain `1fr` every table had before, so tables that set none of them
 * render exactly as they always did.
 */
function columnTrack(col: VirtualColumnDef): string {
  if (col.width !== undefined) return col.width
  if (col.minWidth === undefined && col.fr === undefined) return '1fr'
  return `minmax(${col.minWidth ?? 0}px, ${col.fr ?? 1}fr)`
}

export interface VirtualAdminTableProps<T> {
  data: T[]
  columns: VirtualColumnDef[]
  renderRow: (row: T, index: number) => ReactNode
  onRowClick?: (row: T, index: number) => void
  rowClassName?: string
  containerHeight?: string
  estimateSize?: () => number
  loading?: boolean
  emptyMessage?: string
}

export function VirtualAdminTable<T>({
  data,
  columns,
  renderRow,
  onRowClick,
  rowClassName,
  containerHeight = 'calc(100vh - 280px)',
  estimateSize = () => 48,
  loading,
  emptyMessage = 'No data',
}: VirtualAdminTableProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize,
    overscan: 5,
  })

  const gridTemplate = columns.map(columnTrack).join(' ')

  // The header and every row share one template; a table whose columns have
  // minimums is as wide as their sum, and scrolls sideways as one piece.
  const tableMinWidth = columns.reduce((sum, col) => sum + (col.minWidth ?? 0), 0)
  const minWidthStyle = columns.some((col) => col.minWidth !== undefined)
    ? { minWidth: `${tableMinWidth}px` }
    : undefined

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading...
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div
      ref={parentRef}
      data-virtual-container
      className="relative overflow-auto rounded-md border"
      style={{ height: containerHeight }}
    >
      <div role="table" className="w-full text-sm" style={minWidthStyle}>
        {/* Header */}
        <div role="rowgroup" className="sticky top-0 z-10 bg-muted/50 border-b">
          <div
            role="row"
            className="grid"
            style={{ gridTemplateColumns: gridTemplate, ...minWidthStyle }}
          >
            {columns.map((col) => (
              <div
                key={col.key}
                role="columnheader"
                className={cn(
                  'h-12 px-4 flex items-center font-medium text-muted-foreground',
                  col.className,
                )}
              >
                {col.header}
              </div>
            ))}
          </div>
        </div>

        {/* Virtual body */}
        <div
          role="rowgroup"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = data[virtualRow.index]
            return (
              <div
                key={virtualRow.index}
                role="row"
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  'grid border-b transition-colors hover:bg-muted/50',
                  rowClassName,
                )}
                style={{
                  gridTemplateColumns: gridTemplate,
                  ...minWidthStyle,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                onClick={
                  onRowClick
                    ? () => onRowClick(row, virtualRow.index)
                    : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onRowClick(row, virtualRow.index)
                        }
                      }
                    : undefined
                }
              >
                {renderRow(row, virtualRow.index)}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
